"use client";

import React, { createContext, useMemo } from "react";

import { useConsent } from "@/core/consent";
import { getOrCreateVisitorId } from "@/core/identity/visitorId";
import { telemetry } from "@/core/analytics/telemetry/telemetry";

import { AnalyticsEventMap, AnalyticsTraits } from "../analytics.types";

type AnalyticsProviderProps = {
  readonly children: React.ReactNode;
};

/**
 * The app's analytics door. Every call is a publication on the instrumentation
 * bus (`analytics/telemetry`): the segment sink loads Segment only once
 * analytics consent activates it and reads the decision off `consent.changed`
 * itself, so nothing here imports a vendor module or checks consent. The route's page view is NOT
 * published from here either — the root layout's `instrumentation/PageViewReporter`
 * owns it, reaching the route groups this provider is never mounted on (the
 * `(prelaunch)` home page ships no provider tree at all). Publishing it here
 * too would double every page view on the routes that do mount this.
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps): React.ReactElement {
  const { hasConsent } = useConsent();

  const isAnalyticsAllowed = useMemo(() => hasConsent("analytics"), [hasConsent]);

  const isAdvertisingAllowed = useMemo(() => hasConsent("advertising"), [hasConsent]);

  const isDataSharingAllowed = useMemo(() => hasConsent("dataSharing"), [hasConsent]);

  const isFunctionalAllowed = useMemo(() => hasConsent("functional"), [hasConsent]);

  // No consent check here on purpose: the bus is the one owner of "may this
  // vendor learn this" — a publication reaches a sink only while the bus has
  // it active, and the segment sink clears its own identity on deactivation.
  // The `is*Allowed` flags stay on the context for UI that reads them.
  const trackEvent = (
    event: keyof AnalyticsEventMap,
    properties: AnalyticsEventMap[keyof AnalyticsEventMap],
  ) => {
    telemetry.trackEvent(event, properties);
  };

  const identify = (userId: string, traits: AnalyticsTraits) => {
    telemetry.identify(userId, traits);
  };

  /**
   * The anonymous identity that assessments and leads are keyed on: the
   * first-party visitor id. Segment loads only once analytics consent
   * activates its sink, so its id cannot be the door to starting an
   * assessment (a "necessary only" visitor would never get one). The segment
   * sink hands this same id to Segment on activation, so the two agree
   * whenever both exist.
   */
  const getAnonymousId = () => Promise.resolve(getOrCreateVisitorId() ?? undefined);

  const value = {
    isAnalyticsAllowed,
    isAdvertisingAllowed,
    isDataSharingAllowed,
    isFunctionalAllowed,
    trackEvent,
    identify,
    reset: telemetry.reset,
    getAnonymousId,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

type AnalyticsContext = {
  readonly isAnalyticsAllowed: boolean;
  readonly isAdvertisingAllowed: boolean;
  readonly isDataSharingAllowed: boolean;
  readonly isFunctionalAllowed: boolean;
  readonly trackEvent: (
    event: keyof AnalyticsEventMap,
    properties: AnalyticsEventMap[keyof AnalyticsEventMap],
  ) => void;
  readonly identify: (userId: string, traits: AnalyticsTraits) => void;
  readonly reset: () => void;
  readonly getAnonymousId: () => Promise<string | undefined>;
};

export const AnalyticsContext = createContext<AnalyticsContext>({} as AnalyticsContext);
